/**
 * Excel Export Service
 * Handles exporting data to Excel format using xlsx library
 */

import * as XLSX from 'xlsx';

class ExcelExportService {
  /**
   * Export data to Excel file
   * @param {Array} data - Array of objects to export
   * @param {string} filename - Name of the file (without extension)
   * @param {string} sheetName - Name of the worksheet
   * @param {Object} options - Additional options for formatting
   */
  exportToExcel(data, filename = 'export', sheetName = 'Sheet1', options = {}) {
    try {
      if (!data || data.length === 0) {
        throw new Error('No data to export');
      }

      // Create a new workbook
      const workbook = XLSX.utils.book_new();

      // Convert data to worksheet
      const worksheet = XLSX.utils.json_to_sheet(data, {
        header: options.headers || Object.keys(data[0]),
        ...options
      });

      // Set column widths if provided
      if (options.columnWidths) {
        worksheet['!cols'] = options.columnWidths.map(width => ({ wch: width }));
      }

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      // Generate Excel file and trigger download
      const excelBuffer = XLSX.write(workbook, { 
        bookType: 'xlsx', 
        type: 'array',
        compression: true 
      });

      // Create blob and download
      const blob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, message: 'Excel file exported successfully' };
    } catch (error) {
      console.error('Excel export error:', error);
      return { success: false, message: error.message };
    }
  }

  /**
   * Export redemption report data to Excel
   * @param {Array} redemptionData - Redemption data array
   * @param {Object} filters - Applied filters (center, dates, etc.)
   * @param {Object} summary - Summary data
   */
  exportRedemptionReport(redemptionData, filters = {}, summary = {}) {
    try {
      if (!redemptionData || redemptionData.length === 0) {
        throw new Error('No redemption data to export');
      }

      // Prepare data for export
      const exportData = redemptionData.map((record, index) => ({
        'S.No': index + 1,
        'Receipt No': record.receipt_no || '-',
        'Redeemed On': record.redeemed_on || '-',
        'Patient Name': record.patient_name || '-',
        'Doctor Name': record.doctor_name || '-',
        'Prescription Date': record.prescription_issued_on || '-',
        'Discount': record.discount_allowed || '-',
        'Hospital': record.hospital_name || '-',
        'IRS Amount': parseFloat(record.irs_redeemed_amount || 0).toFixed(2),
        'Beneficiary Cash': parseFloat(record.bnf_cash || 0).toFixed(2),
        'Total Amount': (parseFloat(record.irs_redeemed_amount || 0) + parseFloat(record.bnf_cash || 0)).toFixed(2),
        'Print Status': this.getPrintStatus(record.receipt_no)
      }));

      // Add summary row
      const totalIrsAmount = redemptionData.reduce((sum, record) => sum + parseFloat(record.irs_redeemed_amount || 0), 0);
      const totalBeneficiaryCash = redemptionData.reduce((sum, record) => sum + parseFloat(record.bnf_cash || 0), 0);
      const grandTotal = totalIrsAmount + totalBeneficiaryCash;

      exportData.push({
        'S.No': '',
        'Receipt No': '',
        'Redeemed On': '',
        'Patient Name': '',
        'Doctor Name': '',
        'Prescription Date': '',
        'Discount': '',
        'Hospital': '',
        'IRS Amount': totalIrsAmount.toFixed(2),
        'Beneficiary Cash': totalBeneficiaryCash.toFixed(2),
        'Total Amount': grandTotal.toFixed(2),
        'Print Status': 'TOTALS'
      });

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const centerName = filters.centerName ? filters.centerName.replace(/[^a-zA-Z0-9]/g, '_') : 'All_Centers';
      const filename = `Redemption_Report_${centerName}_${filters.fromDate || 'all'}_to_${filters.toDate || 'all'}_${timestamp}`;

      // Export options
      const options = {
        columnWidths: [5, 15, 15, 20, 20, 15, 10, 20, 12, 15, 12, 12],
        headers: [
          'S.No', 'Receipt No', 'Redeemed On', 'Patient Name', 'Doctor Name',
          'Prescription Date', 'Discount', 'Hospital', 'IRS Amount', 
          'Beneficiary Cash', 'Total Amount', 'Print Status'
        ]
      };

      return this.exportToExcel(exportData, filename, 'Redemption Report', options);
    } catch (error) {
      console.error('Redemption report export error:', error);
      return { success: false, message: error.message };
    }
  }

  /**
   * Get print status for a receipt
   * @param {string} receiptNo - Receipt number
   */
  getPrintStatus(receiptNo) {
    try {
      const printedReceipts = JSON.parse(localStorage.getItem('printedReceipts') || '[]');
      const printedReceipt = printedReceipts.find(r => r.id === receiptNo);
      return printedReceipt ? 'Printed' : 'Not Printed';
    } catch (error) {
      return 'Unknown';
    }
  }

  /**
   * Export multiple sheets to Excel
   * @param {Array} sheets - Array of sheet objects {name, data, options}
   * @param {string} filename - Name of the file
   */
  exportMultipleSheets(sheets, filename = 'export') {
    try {
      if (!sheets || sheets.length === 0) {
        throw new Error('No sheets to export');
      }

      const workbook = XLSX.utils.book_new();

      sheets.forEach(sheet => {
        const worksheet = XLSX.utils.json_to_sheet(sheet.data, sheet.options || {});
        
        if (sheet.columnWidths) {
          worksheet['!cols'] = sheet.columnWidths.map(width => ({ wch: width }));
        }

        XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name);
      });

      const excelBuffer = XLSX.write(workbook, { 
        bookType: 'xlsx', 
        type: 'array',
        compression: true 
      });

      const blob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, message: 'Excel file with multiple sheets exported successfully' };
    } catch (error) {
      console.error('Multiple sheets export error:', error);
      return { success: false, message: error.message };
    }
  }

  /**
   * Export detailed redemption report with summary sheet
   * @param {Array} redemptionData - Redemption data
   * @param {Object} filters - Applied filters
   * @param {Object} summary - Summary data
   */
  exportDetailedRedemptionReport(redemptionData, filters = {}, summary = {}) {
    try {
      const sheets = [];

      // Main data sheet
      const mainData = redemptionData.map((record, index) => ({
        'S.No': index + 1,
        'Receipt No': record.receipt_no || '-',
        'Voucher No': record.voucher_no || '-',
        'Redeemed On': record.redeemed_on || '-',
        'Patient Name': record.patient_name || '-',
        'Doctor Name': record.doctor_name || '-',
        'Prescription Date': record.prescription_issued_on || '-',
        'Discount': record.discount_allowed || '-',
        'Hospital': record.hospital_name || '-',
        'IRS Amount': parseFloat(record.irs_redeemed_amount || 0).toFixed(2),
        'Beneficiary Cash': parseFloat(record.bnf_cash || 0).toFixed(2),
        'Total Amount': (parseFloat(record.irs_redeemed_amount || 0) + parseFloat(record.bnf_cash || 0)).toFixed(2),
        'Print Status': this.getPrintStatus(record.receipt_no)
      }));

      sheets.push({
        name: 'Redemption Data',
        data: mainData,
        columnWidths: [5, 15, 15, 20, 20, 15, 10, 20, 12, 15, 12, 12]
      });

      // Summary sheet
      const totalIrsAmount = redemptionData.reduce((sum, record) => sum + parseFloat(record.irs_redeemed_amount || 0), 0);
      const totalBeneficiaryCash = redemptionData.reduce((sum, record) => sum + parseFloat(record.bnf_cash || 0), 0);
      const grandTotal = totalIrsAmount + totalBeneficiaryCash;
      const printedCount = redemptionData.filter(record => this.getPrintStatus(record.receipt_no) === 'Printed').length;

      const summaryData = [
        { 'Metric': 'Total Records', 'Value': redemptionData.length },
        { 'Metric': 'Total IRS Amount', 'Value': `$${totalIrsAmount.toFixed(2)}` },
        { 'Metric': 'Total Beneficiary Cash', 'Value': `$${totalBeneficiaryCash.toFixed(2)}` },
        { 'Metric': 'Grand Total', 'Value': `$${grandTotal.toFixed(2)}` },
        { 'Metric': 'Printed Receipts', 'Value': printedCount },
        { 'Metric': 'Unprinted Receipts', 'Value': redemptionData.length - printedCount },
        { 'Metric': 'Service Center', 'Value': filters.centerName || 'All Centers' },
        { 'Metric': 'From Date', 'Value': filters.fromDate || 'N/A' },
        { 'Metric': 'To Date', 'Value': filters.toDate || 'N/A' },
        { 'Metric': 'Generated On', 'Value': new Date().toLocaleString() }
      ];

      sheets.push({
        name: 'Summary',
        data: summaryData,
        columnWidths: [25, 20]
      });

      // Generate filename
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const centerName = filters.centerName ? filters.centerName.replace(/[^a-zA-Z0-9]/g, '_') : 'All_Centers';
      const filename = `Detailed_Redemption_Report_${centerName}_${filters.fromDate || 'all'}_to_${filters.toDate || 'all'}_${timestamp}`;

      return this.exportMultipleSheets(sheets, filename);
    } catch (error) {
      console.error('Detailed redemption report export error:', error);
      return { success: false, message: error.message };
    }
  }
}

// Create singleton instance
const excelExportService = new ExcelExportService();

export default excelExportService;
